"use client";

/**
 * MedMock — animated mock of the askFinz Med workspace for the /apps/med
 * marketing page.
 *
 * Clinical-literature reviewer surface. THREE panes:
 *   left   · 5 retrieved papers, PubMed-style cites + relevance bars
 *   centre · a clinical-note draft assembling section-by-section with
 *            [1][3]-style citations materialising inline
 *   right  · a "sensitive workspace" badge + agent action log
 *            (retrieved → ranked → summarised → drafted → flagged)
 *
 * STRICT NO-PII rule. Patient is referred to as `Case 8821 · 42M · post-op
 * day 3` only — no names, no DOB, no MRNs. The status pill is
 * "Pending physician review" in brand red and never reads "Done".
 *
 * Lives in askfinz.com only.
 */
import { useEffect, useRef, useState } from "react";
import {
  FileText,
  ShieldCheck,
  BookOpen,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Search,
  ListOrdered,
  FilePenLine,
  Flag,
} from "lucide-react";

/* ────────── Retrieved papers ────────── */

type Paper = {
  id: number;
  title: string;
  cite: string;        // "NEJM · 2024 · DOI:10.xxxx/xxxxx"
  relevance: number;   // 0..1
};

const PAPERS: Paper[] = [
  {
    id: 1,
    title: "Early ambulation after major abdominal surgery — a multicentre cohort",
    cite: "NEJM · 2024 · DOI:10.xxxx/abc.2024.0142",
    relevance: 0.94,
  },
  {
    id: 2,
    title: "Post-operative venous thromboembolism prophylaxis in low-risk patients",
    cite: "Lancet · 2023 · DOI:10.xxxx/def.2023.7741",
    relevance: 0.88,
  },
  {
    id: 3,
    title: "Inflammatory markers and recovery trajectory in laparoscopic cohorts",
    cite: "JAMA Surg · 2025 · DOI:10.xxxx/ghi.2025.0033",
    relevance: 0.81,
  },
  {
    id: 4,
    title: "Day-three CRP as a predictor of anastomotic complications",
    cite: "Ann Surg · 2022 · DOI:10.xxxx/jkl.2022.5520",
    relevance: 0.74,
  },
  {
    id: 5,
    title: "Patient-reported outcomes at 30 days — systematic review",
    cite: "BMJ · 2024 · DOI:10.xxxx/mno.2024.1186",
    relevance: 0.66,
  },
];

/* ────────── Clinical note sections (sanitised, NO PII) ────────── */

type Section = {
  heading: string;
  body: string;
  cites: number[];
};

const SECTIONS: Section[] = [
  {
    heading: "History",
    body:
      "Case identifier 8821 · 42M · post-operative day 3 following elective laparoscopic procedure. No documented complications intra-operatively. Vitals trending within expected post-op range; mobilisation initiated day 1.",
    cites: [1],
  },
  {
    heading: "Investigations",
    body:
      "Day-3 CRP elevated relative to expected curve for cohort; WBC trending down. Imaging not currently indicated by protocol. Anticoagulation continued per VTE risk stratification.",
    cites: [2, 4],
  },
  {
    heading: "Assessment",
    body:
      "Recovery trajectory broadly consistent with reference cohort. Mildly elevated inflammatory marker warrants close observation; literature suggests day-3 CRP carries modest predictive value at this magnitude but does not, in isolation, indicate intervention.",
    cites: [1, 3, 4],
  },
  {
    heading: "Plan",
    body:
      "Continue current post-op pathway. Re-check CRP at 24h. Maintain early ambulation. Flag for physician review of inflammatory trend before discharge planning; do not progress diet pending re-assessment.",
    cites: [1, 5],
  },
];

/* ────────── Agent action log ────────── */

type Action = {
  icon: typeof Search;
  label: string;
  detail?: string;
};

const ACTIONS: Action[] = [
  { icon: Search,       label: "Retrieved literature",  detail: "12 papers · 5 retained" },
  { icon: ListOrdered,  label: "Ranked by relevance",   detail: "weighted by recency + cohort match" },
  { icon: BookOpen,     label: "Summarised top 5",      detail: "extractive · faithful to source" },
  { icon: FilePenLine,  label: "Drafted clinical note", detail: "4 sections · 7 citations" },
  { icon: Flag,         label: "Flagged for review",    detail: "elevated day-3 CRP outside expected curve" },
];

/* ────────── Component ────────── */

// First-paint snapshot: paper list fully populated, clinical note 50%
// drafted (history + investigations sections in), agent actions partway
// through. The script extends to assessment + plan + flag for review.
const INITIAL_PAPER_STEP = PAPERS.length;
const INITIAL_SECTION_STEP = 2; // history + investigations already drafted
const INITIAL_ACTION_STEP = 3;  // retrieved + ranked + summarised already done

export function MedMock() {
  const [paperStep, setPaperStep]     = useState(INITIAL_PAPER_STEP); // bars fill in one at a time
  const [sectionStep, setSectionStep] = useState(INITIAL_SECTION_STEP); // sections visible
  const [actionStep, setActionStep]   = useState(INITIAL_ACTION_STEP); // actions visible
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const timers: number[] = [];
    const schedule = (fn: () => void, delay: number) => {
      timers.push(window.setTimeout(fn, delay));
    };

    const runCycle = () => {
      // Loop reset = same partial-fill snapshot, never empty.
      setPaperStep(INITIAL_PAPER_STEP);
      setSectionStep(INITIAL_SECTION_STEP);
      setActionStep(INITIAL_ACTION_STEP);

      // Remaining actions (draft note + flag for review).
      for (let i = INITIAL_ACTION_STEP; i < ACTIONS.length; i += 1) {
        schedule(() => setActionStep(i + 1), 450 + (i - INITIAL_ACTION_STEP) * 380);
      }

      // Remaining note sections (assessment + plan).
      const noteStart = 400;
      for (let i = INITIAL_SECTION_STEP; i < SECTIONS.length; i += 1) {
        schedule(() => setSectionStep(i + 1), noteStart + (i - INITIAL_SECTION_STEP) * 700);
      }

      // Loop after dwell on completion.
      const end = Math.max(
        noteStart + (SECTIONS.length - INITIAL_SECTION_STEP) * 700,
        450 + (ACTIONS.length - INITIAL_ACTION_STEP) * 380
      );
      schedule(runCycle, end + 3500);
    };

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        runCycle();
        obs.disconnect();
      },
      { threshold: 0.22 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      timers.forEach(clearTimeout);
    };
  }, []);

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[20px] border hairline-strong bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 h-10 border-b hairline">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="ml-3 text-[11px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
            med.askfinz.ai · live
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
            <ShieldCheck className="h-3 w-3" />
            Isolated workspace
          </span>
        </div>

        {/* Case identifier strip — NO PII */}
        <div className="px-4 py-2.5 border-b hairline flex items-center gap-3 text-[11px] font-mono text-foreground/65 flex-wrap">
          <span className="text-[10px] tracking-[0.16em] uppercase text-foreground/65 not-italic">Case</span>
          <span className="text-foreground/85">8821</span>
          <span className="text-foreground/30">·</span>
          <span>42M</span>
          <span className="text-foreground/30">·</span>
          <span>post-op day 3</span>
          <span className="text-foreground/30">·</span>
          <span className="inline-flex items-center gap-1 text-foreground/65">
            <span className="text-[9px] tracking-[0.16em] uppercase text-foreground/65">CRP</span>
            <span>84 mg/L</span>
          </span>
          <span className="text-foreground/30">·</span>
          <span className="inline-flex items-center gap-1 text-foreground/65">
            <span className="text-[9px] tracking-[0.16em] uppercase text-foreground/65">WBC</span>
            <span>10.2 ×10⁹</span>
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 rounded-full border hairline-strong bg-[hsl(var(--accent))]/8 px-2.5 py-1 text-[10px] tracking-[0.16em] uppercase text-[hsl(var(--accent))] not-italic">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] animate-pulse" />
            Pending physician review
          </span>
        </div>

        <div className="grid grid-cols-12 min-h-[500px]">
          {/* Retrieved papers */}
          <div className="col-span-12 md:col-span-4 border-r hairline">
            <div className="px-4 py-2.5 border-b hairline flex items-center gap-2 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
              <BookOpen className="h-3 w-3" />
              Retrieved literature
              <span className="ml-auto font-mono normal-case tracking-normal text-foreground/65">
                {Math.min(paperStep, PAPERS.length)}/{PAPERS.length}
              </span>
            </div>
            <div className="p-3 space-y-2">
              {PAPERS.map((p, i) => {
                const visible = i < paperStep;
                return (
                  <div
                    key={p.id}
                    className={
                      "rounded-lg border p-3 transition-opacity " +
                      (visible ? "hairline opacity-100 animate-[blur-fade-in_0.4s_ease-out]" : "hairline opacity-30")
                    }
                  >
                    <div className="flex items-start gap-2">
                      <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border hairline-strong text-[10px] font-mono text-foreground/65">
                        {p.id}
                      </span>
                      <div className="min-w-0">
                        <div className="text-[12px] leading-snug text-foreground/85">{p.title}</div>
                        <div className="mt-1 text-[10px] font-mono text-foreground/65 truncate">{p.cite}</div>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <div className="relative h-1 flex-1 rounded-full bg-foreground/10 overflow-hidden">
                        <div
                          className="absolute inset-y-0 left-0 bg-foreground/65 transition-[width] duration-700"
                          style={{ width: visible ? `${Math.round(p.relevance * 100)}%` : "0%" }}
                        />
                      </div>
                      <span className="text-[10px] font-mono text-foreground/65">
                        {visible ? `${Math.round(p.relevance * 100)}%` : "—"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Clinical note draft */}
          <div className="col-span-12 md:col-span-5 flex flex-col">
            <div className="px-4 py-2.5 border-b hairline flex items-center gap-2 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
              <FileText className="h-3 w-3" />
              Clinical note · draft
              <span className="ml-auto font-mono normal-case tracking-normal text-foreground/65">
                {Math.min(sectionStep, SECTIONS.length)}/{SECTIONS.length} sections
              </span>
            </div>
            <div className="flex-1 px-5 py-4 space-y-4 text-[12.5px] leading-[1.65] text-foreground/85">
              {SECTIONS.slice(0, sectionStep).map((s, i) => (
                <div key={i} className="animate-[blur-fade-in_0.5s_ease-out]">
                  <div className="text-[10px] tracking-[0.16em] uppercase text-foreground/65 mb-1">
                    {s.heading}
                  </div>
                  <p>
                    {s.body}
                    <span className="ml-1 inline-flex gap-0.5 align-baseline">
                      {s.cites.map((c) => (
                        <span
                          key={c}
                          className="inline-block min-w-[1.4em] text-center text-[10px] text-foreground/65 rounded-sm border hairline px-1 leading-tight animate-[blur-fade-in_0.4s_ease-out]"
                        >
                          {c}
                        </span>
                      ))}
                    </span>
                  </p>
                </div>
              ))}
              {sectionStep === 0 && (
                <div className="text-foreground/65 text-[11px] italic">Awaiting retrieval…</div>
              )}
              {sectionStep > 0 && sectionStep < SECTIONS.length && (
                <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-foreground/65">
                  <Sparkles className="h-3 w-3" />
                  Drafting next section…
                </div>
              )}
            </div>

            {/* Reviewer banner */}
            <div className="border-t hairline px-4 py-2.5 flex items-center gap-2 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
              <AlertTriangle className="h-3 w-3 text-foreground/65" />
              <span>Not for clinical use without physician review</span>
            </div>
          </div>

          {/* Workspace integrity + action log */}
          <div className="col-span-12 md:col-span-3 flex flex-col border-t md:border-t-0 md:border-l hairline bg-foreground/[0.02]">
            <div className="px-3 py-2.5 border-b hairline">
              <div className="rounded-md border hairline-strong bg-background p-2.5">
                <div className="flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65 mb-1.5">
                  <ShieldCheck className="h-3 w-3" />
                  Sensitive workspace
                </div>
                <ul className="space-y-1 text-[11px] text-foreground/70">
                  <li className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-3 w-3 text-foreground/65" />
                    Isolated database
                  </li>
                  <li className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-3 w-3 text-foreground/65" />
                    Audit log on
                  </li>
                  <li className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-3 w-3 text-foreground/65" />
                    No model training on data
                  </li>
                </ul>
              </div>
            </div>

            <div className="px-3 py-2.5 border-b hairline flex items-center gap-2 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
              <Sparkles className="h-3 w-3" />
              Agent actions
            </div>
            <div className="flex-1 p-3 space-y-2.5 text-[12px]">
              {ACTIONS.map((a, i) => {
                const status: "queued" | "running" | "done" =
                  i < actionStep - 1 ? "done" : i === actionStep - 1 ? "running" : "queued";
                const Icon = a.icon;
                return (
                  <div key={i} className="flex items-start gap-2">
                    <span className="mt-0.5 shrink-0">
                      {status === "done" ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-foreground/70" />
                      ) : status === "running" ? (
                        <Icon className="h-3.5 w-3.5 text-foreground/85 animate-pulse" />
                      ) : (
                        <span className="block h-3.5 w-3.5 rounded-full border hairline-strong" />
                      )}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className={status === "queued" ? "text-foreground/65" : "text-foreground/85"}>
                        {a.label}
                      </div>
                      {a.detail && status !== "queued" && (
                        <div className="text-[10px] font-mono text-foreground/65 mt-0.5 leading-snug">{a.detail}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
